import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, catchError, map, of, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { extractApiError } from '../http/api-error';
import { AuthResponseDto, LoginRequestDto, UserSummaryDto } from './auth.models';

export interface MicrosoftSignInResult {
  success: boolean;
  error?: string;
}

/** What the backend's Microsoft sign-in page posts back to this window. */
interface MicrosoftSsoMessage {
  type: string;
  success: boolean;
  error?: string | null;
  accessToken?: string;
  refreshToken?: string;
  expiresAt?: string;
  user?: UserSummaryDto;
}

const TOKEN_KEY = 'EBP_access_token';
const USER_KEY = 'EBP_user';
const ADMIN_ROLES = ['admin', 'superadmin'];

/** Signing in and out, and the signed-in user (kept in local storage between visits). */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = `${environment.apiBaseUrl}/auth`;

  readonly currentUser = signal<UserSummaryDto | null>(readStored<UserSummaryDto>(USER_KEY));
  readonly accessToken = signal<string | null>(localStorage.getItem(TOKEN_KEY));
  readonly isAuthenticated = computed(() => !!this.accessToken());
  /** From the role in the token's user; the backend checks every admin call itself. */
  readonly isAdmin = computed(() => ADMIN_ROLES.includes(this.currentUser()?.role?.toLowerCase() ?? ''));

  /** Signs in with a username or email and password. Emits whether it worked. */
  login(usernameOrEmail: string, password: string): Observable<boolean> {
    const payload: LoginRequestDto = { usernameOrEmail: usernameOrEmail.trim(), password };
    return this.http.post<AuthResponseDto>(`${this.apiUrl}/login`, payload).pipe(
      tap((response) => this.setSession(response)),
      map(() => true),
      catchError(() => of(false))
    );
  }

  /** Opens the backend's Microsoft sign-in in a popup and waits for its answer. */
  signInWithMicrosoft(): Observable<MicrosoftSignInResult> {
    return new Observable<MicrosoftSignInResult>((subscriber) => {
      const width = 520;
      const height = 660;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        `${environment.backendOrigin}/api/auth/microsoft-login`,
        'EBPMicrosoftSignIn',
        `width=${width},height=${height},left=${left},top=${top}`
      );

      if (!popup) {
        subscriber.next({ success: false, error: 'Popup blocked. Please allow popups and try again.' });
        subscriber.complete();
        return;
      }

      const onMessage = (event: MessageEvent<MicrosoftSsoMessage>) => {
        const data = event.data;
        if (event.origin !== environment.backendOrigin || data?.type !== 'pebp-microsoft-sso') return;

        window.removeEventListener('message', onMessage);
        if (data.success && data.accessToken && data.user) {
          this.setSession({
            accessToken: data.accessToken,
            refreshToken: data.refreshToken ?? '',
            expiresAt: data.expiresAt ?? new Date().toISOString(),
            user: data.user,
          });
          subscriber.next({ success: true });
        } else {
          subscriber.next({ success: false, error: data.error ?? 'Microsoft sign-in failed.' });
        }
        subscriber.complete();
      };

      window.addEventListener('message', onMessage);
      return () => window.removeEventListener('message', onMessage);
    });
  }

  /** Emits null on success, otherwise the error message. currentPassword is needed when a password already exists. */
  setPassword(newPassword: string, currentPassword?: string): Observable<string | null> {
    return this.http.post(`${this.apiUrl}/set-password`, { newPassword, currentPassword }).pipe(
      map(() => {
        const user = this.currentUser();
        if (user) this.storeUser({ ...user, hasPassword: true });
        return null;
      }),
      catchError((err) => of(extractApiError(err, 'Could not set password. Please try again.')))
    );
  }

  logout(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    this.accessToken.set(null);
    this.currentUser.set(null);
  }

  private setSession(auth: AuthResponseDto): void {
    localStorage.setItem(TOKEN_KEY, auth.accessToken);
    this.accessToken.set(auth.accessToken);
    this.storeUser(auth.user);
  }

  private storeUser(user: UserSummaryDto): void {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    this.currentUser.set(user);
  }
}

function readStored<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}
